import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import "../assets/css/EditLead.css";
import "../assets/css/AddLead.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import QuoteGenerator from "../components/QuoteGenerator";

function EditBusinessLead({ leadId, onBack, onUpdateLead }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    companyName: "",
    domain: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    communicationLanguage: "",
    source: "",
    status: "",
    isQuoteSent: false,
    services: [],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zipCode: ""
  });

  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: ""
  });
  const [followupId, setFollowupId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [customServices, setCustomServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const sourceDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");
  const statusDropdownRef = useRef(null);

  // Fetch sources
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const response = await axiosInstance.get("/lead-sources");
        if (response.data && Array.isArray(response.data)) {
          setSources(response.data);
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  // Fetch lead statuses for dropdown (same API as filters/new lead)
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/lead-statuses");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? ""
          }))
          .filter((s) => s.id != null && s.name.trim() !== "");
        setLeadStatuses(mapped);
      } catch (error) {
        console.error("Error fetching lead statuses for edit lead:", error);
        setLeadStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchStatuses();
  }, []);

  // Fetch domains for dropdown (same API as filters/new lead)
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const response = await axiosInstance.get("/domains");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (error) {
        console.error("Error fetching domains for edit lead:", error);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axiosInstance.get("/countries");
        if (response.data && Array.isArray(response.data)) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Fetch states when country is selected (same as Add Lead: /states/{country_id})
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountryId) {
        setStates([]);
        setFormData(prev => ({ ...prev, state: "" }));
        return;
      }
      setLoadingStates(true);
      try {
        const response = await axiosInstance.get(`/states/${selectedCountryId}`);
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        try {
          const altResponse = await axiosInstance.get(`/api/states/${selectedCountryId}`);
          if (altResponse.data && Array.isArray(altResponse.data)) {
            setStates(altResponse.data);
          }
        } catch (altError) {
          console.error("Error with alternative endpoint:", altError);
          setStates([]);
        }
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [selectedCountryId]);

  // Fetch cities when state is selected (same as Add Lead: /cities/{state_id})
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedStateId) {
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
        return;
      }
      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${selectedStateId}`);
        if (response.data && Array.isArray(response.data)) {
          setCities(response.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [selectedStateId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) { setSourceDropdownOpen(false); setSourceSearchQuery(""); }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) { setStatusDropdownOpen(false); setStatusSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch lead details and pre-fill form
  useEffect(() => {
    if (!leadId) return;
    
    const fetchLeadDetails = async (categories, countriesList = [], domainsList = []) => {
      setLoading(true);
      try {
        // Try single lead endpoint
        let leadObj = null;
        let addressObj = {};

        try {
          // Business leads endpoint
          const endpoint = `/lead/${leadId}`;
          const dataKey = "lead";
          const idField = "Lead_Id";
          const allEndpoint = "/business-leads";
          
          const response = await axiosInstance.get(endpoint);
          if (response.data) {
            if (response.data[dataKey]) {
              leadObj = response.data[dataKey];
              addressObj = response.data.address || {};
            } else {
              leadObj = response.data;
            }
          }
        } catch (err) {
          // Fallback: fetch all and find
          const dataKey = "lead";
          const idField = "Lead_Id";
          const allEndpoint = "/business-leads";
          
          const allResponse = await axiosInstance.get(allEndpoint);
          if (allResponse.data && Array.isArray(allResponse.data)) {
            const found = allResponse.data.find(item => {
              const l = item[dataKey] || item;
              return l[idField] === leadId;
            });
            if (found) {
              leadObj = found[dataKey] || found;
              addressObj = found.address || {};
            }
          }
        }

        if (leadObj) {
          setLeadName(`${leadObj.First_Name || ""} ${leadObj.Last_Name || ""}`.trim());

          // Parse phone: split country code and number for separate fields
          const apiCountryCode = (leadObj.Country_Code || leadObj.country_code || "").trim();
          const rawPhone = String(leadObj.Phone || leadObj.phone || "").trim();
          let parsedCountryCode = apiCountryCode;
          let normalizedPhone = rawPhone;

          if (rawPhone && apiCountryCode) {
            const code = apiCountryCode;
            const plain = code.startsWith("+") ? code.slice(1) : code;
            if (rawPhone.startsWith(code) || rawPhone.startsWith(plain)) {
              normalizedPhone = rawPhone.startsWith(code)
                ? rawPhone.slice(code.length).trim()
                : rawPhone.slice(plain.length).trim();
            }
          } else if (rawPhone && countriesList && countriesList.length > 0) {
            const codes = countriesList
              .flatMap((c) => {
                const code = (c.country_code || c.country_short_code || "").trim();
                return code ? [code, code.replace(/^\+/, "")] : [];
              })
              .filter((v, i, a) => a.indexOf(v) === i)
              .sort((a, b) => b.length - a.length);
            for (const code of codes) {
              const plain = code.startsWith("+") ? code.slice(1) : code;
              if (rawPhone.startsWith(code) || rawPhone.startsWith(plain)) {
                parsedCountryCode = code.startsWith("+") ? code : `+${code}`;
                normalizedPhone = rawPhone.startsWith(code)
                  ? rawPhone.slice(code.length).trim()
                  : rawPhone.slice(plain.length).trim();
                break;
              }
            }
          }

          if (parsedCountryCode) {
            const matchingCountry = countriesList.find((c) => {
              const code = (c.country_code || c.country_short_code || "").trim();
              const plain = code.replace(/^\+/, "");
              const parsedPlain = parsedCountryCode.replace(/^\+/, "");
              return code === parsedCountryCode || plain === parsedCountryCode || code === parsedPlain || plain === parsedPlain;
            });
            setPhoneCountryCode(matchingCountry
              ? (matchingCountry.country_code || matchingCountry.country_short_code || "").trim()
              : parsedCountryCode);
          }

          // Business Lead: resolve service names to IDs
          let resolvedServices = [];
          const serviceNames = leadObj.Services || [];
          resolvedServices = serviceNames;
          
          if (categories && categories.length > 0 && serviceNames.length > 0) {
            const allSubcategories = categories.flatMap(cat =>
              (cat.subcategories || [])
            );
            const resolvedIds = serviceNames
              .map(name => {
                // Try exact match first
                let found = allSubcategories.find(sub => sub.subcategory_name === name);
                // If not found, try case-insensitive match
                if (!found) {
                  found = allSubcategories.find(sub => 
                    sub.subcategory_name.toLowerCase() === name.toLowerCase()
                  );
                }
                return found ? found.subcategory_id : null;
              })
              .filter(Boolean);
            
            if (resolvedIds.length > 0) {
              resolvedServices = resolvedIds;
            }
          }

          // Resolve domain: prefer Domain_Id/domain_id, fallback to Domain (name) via domainsList
          let domainValue = "";
          const domainId = leadObj.Domain_Id ?? leadObj.domain_id;
          if (domainId != null && domainId !== "") {
            domainValue = String(domainId);
          } else if (domainsList.length > 0 && (leadObj.Domain || leadObj.domain_name)) {
            const domainName = (leadObj.Domain || leadObj.domain_name || "").trim();
            const matched = domainsList.find(d =>
              String(d.name || "").toLowerCase() === domainName.toLowerCase()
            );
            if (matched) domainValue = String(matched.id);
          }

          setFormData({
            firstName: leadObj.First_Name || "",
            lastName: leadObj.Last_Name || "",
            personalEmail: leadObj.Email || "",
            personalPhone: normalizedPhone,
            companyName: leadObj.Company_Name || "",
            domain: domainValue,
            companyEmail: leadObj.Company_Email || "",
            companyPhone: leadObj.Company_Phone || "",
            website: leadObj.Website || "",
            communicationLanguage: leadObj.Com_Lang || "",
            source: leadObj.Source_Id ? String(leadObj.Source_Id) : "",
            status: leadObj.Status_Id ? String(leadObj.Status_Id) : "",
            isQuoteSent: leadObj.Is_Quote_Sent || false,
            services: resolvedServices, // Store IDs if resolved, otherwise names (will resolve in useEffect)
            addressLine1: addressObj.Address_Line_1 || "",
            addressLine2: addressObj.Address_Line_2 || "",
            city: addressObj.City_Name || "",
            state: addressObj.State_Name || "",
            country: addressObj.Country_Name || "",
            zipCode: addressObj.Zip_Code || ""
          });

          // Set IDs for cascading dropdowns
          if (addressObj.Country_Id) {
            setSelectedCountryId(addressObj.Country_Id);
          }
          if (addressObj.State_Id) {
            setSelectedStateId(addressObj.State_Id);
          }

          // Fetch latest followup
          try {
            const followupRes = await axiosInstance.get(`/followups?lead_id=${leadId}`);
            
            if (followupRes.data && Array.isArray(followupRes.data) && followupRes.data.length > 0) {
              // Sort by Followup_Date descending to get the latest
              const sortedFollowups = followupRes.data.sort((a, b) => {
                const dateA = new Date(a.Followup_Date);
                const dateB = new Date(b.Followup_Date);
                return dateB - dateA; // Descending order
              });
              const latest = sortedFollowups[0];
              // Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
              const formatDateTimeLocal = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              };
              setFollowupData({
                followupDate: formatDateTimeLocal(latest.Followup_Date),
                followupType: latest.Followup_Type || "",
                nextFollowupDate: formatDateTimeLocal(latest.Next_Followup_Date),
                remarks: latest.Remarks || ""
              });
              setFollowupId(latest.Followup_Id || latest.Followup_id);
            }
          } catch (followupError) {
            console.error("Error fetching followup:", followupError);
            // Don't set followup data if fetch fails
          }
        }
      } catch (error) {
        console.error("Error fetching lead details:", error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch service categories first, then fetch lead details
    const fetchServiceCategories = async () => {
      try {
        const response = await axiosInstance.get("/service/categories");
        if (response.data && Array.isArray(response.data)) {
          setServiceCategories(response.data);
          return response.data;
        }
      } catch (error) {
        console.error("Error fetching service categories:", error);
      }
      return [];
    };

    const loadData = async () => {
      const categories = await fetchServiceCategories();
      let countriesList = [];
      let domainsList = [];
      try {
        const countriesRes = await axiosInstance.get("/countries");
        if (countriesRes.data && Array.isArray(countriesRes.data)) {
          countriesList = countriesRes.data;
        }
      } catch (e) {
        console.error("Error fetching countries for phone parsing:", e);
      }
      try {
        const domainsRes = await axiosInstance.get("/domains");
        const raw = Array.isArray(domainsRes.data) ? domainsRes.data : domainsRes.data?.data || [];
        domainsList = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && (d.name || "").trim() !== "");
      } catch (e) {
        console.error("Error fetching domains for lead edit:", e);
      }
      await fetchLeadDetails(categories, countriesList, domainsList);
    };

    if (leadId) {
      loadData();
    }
  }, [leadId]);

  // Fetch services categories
  useEffect(() => {
    const fetchServiceCategories = async () => {
      setLoadingServices(true);
      try {
        const response = await axiosInstance.get("/service/categories");
        if (response.data && Array.isArray(response.data)) {
          setServiceCategories(response.data);
        }
      } catch (error) {
        console.error("Error fetching service categories:", error);
        setServiceCategories([]);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServiceCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "country") {
      const selectedCountry = countries.find(c => c.country_name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.country_id);
      } else {
        setSelectedCountryId(null);
        setSelectedStateId(null);
        setStates([]);
        setCities([]);
        setFormData(prev => ({ ...prev, state: "", city: "" }));
      }
    }

    if (name === "state") {
      const selectedState = states.find(s => s.state_name === value);
      if (selectedState) {
        setSelectedStateId(selectedState.state_id);
      } else {
        setSelectedStateId(null);
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = (subcategoryId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(subcategoryId)
        ? prev.services.filter(id => id !== subcategoryId)
        : [...prev.services, subcategoryId]
    }));
  };

  const handleAddServiceToCategory = (categoryId) => {
    setEditingService({
      id: `custom-${Date.now()}`,
      name: "",
      categoryId
    });
    setIsAddingServiceToCategory(categoryId);
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    setCustomServices(prev => ({
      ...prev,
      [editingService.id]: { name: editingService.name.trim(), categoryId: editingService.categoryId }
    }));
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, editingService.id]
    }));
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleDeleteCustomService = (id) => {
    if (!window.confirm("Remove this custom service?")) return;
    setCustomServices(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== id)
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.personalPhone) {
      alert("Please fill in required fields (First Name and Phone).");
      return;
    }
    if (!formData.source) {
      alert("Please select a source.");
      return;
    }
    if (!formData.services || formData.services.length === 0) {
      alert("Please select at least one service.");
      return;
    }

    let createdBy = 1;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (error) {
      console.error("Error reading user data:", error);
    }

    // Find city_id, state_id, country_id from names
    const selectedCountry = countries.find(c => c.country_name === formData.country);
    const selectedState = states.find(s => s.state_name === formData.state);
    const selectedCity = cities.find(c => c.city_name === formData.city);

    // Business Lead: Use /update-lead endpoint
    const endpoint = `/update-lead/${leadId}`;
    const numericServiceIds = formData.services.filter(s => typeof s === "number");
    const customServiceNames = formData.services
      .filter(s => typeof s === "string")
      .map(id => customServices[id]?.name)
      .filter(Boolean);

    const code = (phoneCountryCode || "").trim();
    const phoneWithCode = code
      ? (code.startsWith("+") ? code : `+${code}`) + (formData.personalPhone || "")
      : (formData.personalPhone || "");

    const statusId = formData.status ? parseInt(formData.status, 10) : null;

    const domainId = formData.domain ? (parseInt(formData.domain, 10) || null) : null;

    const payload = {
      First_Name: formData.firstName,
      Last_Name: formData.lastName,
      Email: formData.personalEmail || null,
      Phone: phoneWithCode,
      Alt_Phone: formData.altPhone || null,
      Source_Id: formData.source ? parseInt(formData.source, 10) : null,
      Status_Id: statusId,
      Domain_Id: domainId,
      domain_id: domainId,
      Company_Name: formData.companyName || null,
      Company_Email: formData.companyEmail || null,
      Company_Phone: formData.companyPhone || null,
      Website: formData.website || null,
      Com_Lang: formData.communicationLanguage || null,
      Is_Quote_Sent: formData.isQuoteSent ? 1 : 0,
      Address_Line_1: formData.addressLine1 || null,
      Address_Line_2: formData.addressLine2 || null,
      City_Id: selectedCity ? selectedCity.city_id : null,
      State_Id: selectedState ? selectedState.state_id : selectedStateId,
      Country_Id: selectedCountry ? selectedCountry.country_id : selectedCountryId,
      Zip_Code: formData.zipCode || null,
      services: numericServiceIds,
      ...(customServiceNames.length > 0 && { custom_services: customServiceNames })
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.put(endpoint, payload);
      if (response.data) {
        // Create/update followup if followup data is provided
        if (followupData.followupDate) {
          try {
            // Format dates to ISO string format (YYYY-MM-DDTHH:mm:ss)
            const formatDateTime = (dateTimeString) => {
              if (!dateTimeString) return null;
              // datetime-local input returns format: YYYY-MM-DDTHH:mm
              // API expects: YYYY-MM-DDTHH:mm:ss
              return dateTimeString + ":00";
            };

            // Get Updated_By from localStorage
            let updatedBy = createdBy;
            try {
              const userStr = localStorage.getItem('user');
              if (userStr) {
                const user = JSON.parse(userStr);
                updatedBy = user.user_id || user.id || createdBy;
              }
            } catch (error) {
              console.error("Error reading user data for Updated_By:", error);
            }

            if (followupId) {
              // Update existing followup for business leads
              const followupPayload = {
                Lead_Id: leadId,
                client_id: null,
                Followup_Date: formatDateTime(followupData.followupDate),
                Followup_Type: followupData.followupType || null,
                Remarks: followupData.remarks || null,
                Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
                Updated_By: updatedBy
              };
              await axiosInstance.put(`/update-followup/${followupId}`, followupPayload);
            } else {
              // Create new followup
              const followupPayload = {
                Lead_Id: leadId,
                client_id: null,
                Training_Lead_Id: null,
                Followup_Date: formatDateTime(followupData.followupDate),
                Followup_Type: followupData.followupType || null,
                Remarks: followupData.remarks || null,
                Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
                Updated_By: updatedBy,
                Created_By: createdBy
              };
              await axiosInstance.post("/create-followup", followupPayload);
            }
          } catch (followupError) {
            console.error("Error creating/updating followup:", followupError);
            // Don't fail the whole update if followup creation/update fails
          }
        }

        alert("Lead updated successfully!");
        // Call parent callback to refresh leads list
        if (onUpdateLead) {
          onUpdateLead();
        }
        // Navigate back to business leads page
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to update lead.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editlead-container">
        <div className="editlead-loading">Loading lead details...</div>
      </div>
    );
  }

  return (
    <div className="editlead-container">
      <button className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back 
      </button>
      <div className="issues-main-card">
      <form className="editlead-form" onSubmit={handleSubmit}>
        {/* Business Lead: same structure as Add Lead - one "Edit Lead" section */}
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Personal Email</label>
              <input type="email" name="personalEmail" value={formData.personalEmail} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  style={{ maxWidth: "120px" }}
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  <option value="">Code</option>
                  {countries.map((country) => {
                    const code = (country.country_code || country.country_short_code || "").trim();
                    const displayCode = code && !code.startsWith("+") ? `+${code}` : code;
                    return (
                      <option
                        key={country.country_id}
                        value={code}
                      >
                        {displayCode}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="tel"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Business</label>
              {domains.length > 0 ? (
                <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                    <span>{loadingDomains ? "Loading businesses..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select business") : "Select business")}</span>
                    <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                  </div>
                  {domainDropdownOpen && !loadingDomains && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search businesses..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select business</div>
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                          <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id) })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                        ))}
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No businesses found</div>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                 
                />
              )}
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Company Phone</label>
              <input type="text" name="companyPhone" value={formData.companyPhone} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input type="text" name="website" value={formData.website} onChange={handleInputChange} />
            </div>
          </div>
        </div>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Communication Language</label>
              <input type="text" name="communicationLanguage" value={formData.communicationLanguage} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Source <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={sourceDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", cursor: "pointer" }} onClick={() => setSourceDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSourceDropdownOpen((o) => !o); } }}>
                  <span>{formData.source ? (sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.source_name || sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.Source_Name || "Select Source") : "Select Source"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${sourceDropdownOpen ? "open" : ""}`} />
                </div>
                {sourceDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search sources..." value={sourceSearchQuery} onChange={(e) => setSourceSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.source ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: "" })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>Select Source</div>
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).map((s) => {
                        const sid = String(s.source_id ?? s.Source_Id ?? s.id);
                        const sname = s.source_name || s.Source_Name || "";
                        return (
                          <div key={sid} className={`domain-filter-option ${String(formData.source) === sid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: sid })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>{sname}</div>
                        );
                      })}
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No sources found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="domain-filter-dropdown" ref={statusDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingStatuses ? 0.6 : 1, cursor: loadingStatuses ? "default" : "pointer" }} onClick={() => !loadingStatuses && setStatusDropdownOpen((o) => !o)} role="button" tabIndex={loadingStatuses ? -1 : 0} onKeyDown={(e) => { if (!loadingStatuses && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setStatusDropdownOpen((o) => !o); } }}>
                  <span>{loadingStatuses ? "Loading statuses..." : (formData.status ? (leadStatuses.find((s) => String(s.id) === String(formData.status))?.name || "Select Status") : "Select Status")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                </div>
                {statusDropdownOpen && !loadingStatuses && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search statuses..." value={statusSearchQuery} onChange={(e) => setStatusSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.status ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: "" })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>Select Status</div>
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).map((s) => (
                        <div key={s.id} className={`domain-filter-option ${String(formData.status) === String(s.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: String(s.id) })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>{s.name}</div>
                      ))}
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No statuses found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" name="isQuoteSent" checked={formData.isQuoteSent} onChange={handleInputChange} />
            <span>Is Quote Sent</span>
            <button
              type="button"
              className="btn-generate-quote"
              onClick={() => navigate("/generate-quote", { state: { customerData: formData, type: "lead" } })}
            >
              Generate Quote
            </button>
          </label>
        </div>

        {/* Services */}
        <div className="form-section">
          <h2 className="section-title">Services <span className="required">*</span></h2>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : (
            <div className="services-card">
              {serviceCategories.map(category => {
                const subs = category.subcategories || [];
                const customForCategory = Object.entries(customServices)
                  .filter(([, s]) => s.categoryId === category.category_id)
                  .map(([id, s]) => ({ id, name: s.name }));
                return (
                  <div key={category.category_id} className="service-category">
                    <h3 className="category-title">{category.category_name}</h3>
                    <div className="services-grid">
                      {subs.map(sub => (
                        <label key={sub.subcategory_id} className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.services.includes(sub.subcategory_id)}
                            onChange={() => handleServiceChange(sub.subcategory_id)}
                          />
                          <span>{sub.subcategory_name}</span>
                        </label>
                      ))}
                      {customForCategory.map(({ id, name }) => (
                        <div key={id} className="addlead-custom-service-row">
                          <label className="service-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.services.includes(id)}
                              onChange={() => handleServiceChange(id)}
                            />
                            <span>{name}</span>
                          </label>
                          <button
                            type="button"
                            className="addlead-custom-service-delete"
                            onClick={() => handleDeleteCustomService(id)}
                            title="Remove"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Address */}
        <div className="form-section">
        
          <div className="form-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select name="country" value={formData.country} onChange={handleInputChange} disabled={loadingCountries}>
                <option value="">{formData.country || "Select Country"}</option>
                {countries.map(country => (
                  <option key={country.country_id} value={country.country_name}>{country.country_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select name="state" value={formData.state} onChange={handleInputChange} disabled={!selectedCountryId || loadingStates}>
                <option value="">{formData.state || "Select State"}</option>
                {states.map(state => (
                  <option key={state.state_id} value={state.state_name}>{state.state_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select name="city" value={formData.city} onChange={handleInputChange} disabled={!selectedStateId || loadingCities}>
                <option value="">{formData.city || "Select City"}</option>
                {cities.map(city => (
                  <option key={city.city_id} value={city.city_name}>{city.city_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} />
            </div>
          </div>
        </div>

        {/* Latest Followup */}
        <div className="editlead-followup-section">
    
          <div className="editlead-followup-grid">
            <div className="form-group">
              <label>Followup Date</label>
              <input
                type="datetime-local"
                name="followupDate"
                value={followupData.followupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Followup Type</label>
              <input
                type="text"
                name="followupType"
                value={followupData.followupType}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Next Followup Date</label>
              <input
                type="datetime-local"
                name="nextFollowupDate"
                value={followupData.nextFollowupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea
                name="remarks"
                value={followupData.remarks}
                onChange={handleFollowupChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="editlead-actions">
          <button type="submit" className="btn-update-lead" disabled={submitting}>
            {submitting ? "Updating..." : "Update Lead"}
          </button>
        </div>
      </form>
      </div>
      {editingService && (
        <div className="addlead-modal-overlay" onClick={handleCancelEditService}>
          <div className="addlead-modal addlead-add-service-modal" onClick={e => e.stopPropagation()}>
            <h3>
              {isAddingServiceToCategory
                ? "Add Service to " +
                  (serviceCategories.find((c) => c.category_id === isAddingServiceToCategory)?.category_name || "Category")
                : "Edit Service"}
            </h3>
            <div className="form-group">
              <label>Service Name</label>
              <input
                type="text"
                value={editingService.name}
                onChange={e => setEditingService({ ...editingService, name: e.target.value })}
               
              />
            </div>
            <div className="addlead-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancelEditService}>Cancel</button>
              <button type="button" className="btn-update-lead" onClick={handleSaveService} disabled={!editingService.name?.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditBusinessLead;
